import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useGetMoveQuery } from "@/store/moves/apis";
import { PaymentLink, PaymentMethod } from "@/types";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { CreditCard, DollarSign, CheckSquare, Smartphone } from "lucide-react";
import { Loading } from "@/components/Loading";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  useProcessFinalPaymentMutation,
  useCreatePaymentLinkMutation,
} from "@/store/charges/apis";
import { CARD_BRANDS } from "@/constants";
import { useSelector } from "react-redux";
import { getSelectedMover } from "@/store/mover/slice";
import { QRCodeSVG } from "qrcode.react";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { checkMobileToken } from "@/store/session/slice";

const NO_FEE_PAYMENT_METHODS = ["cash", "check", "venmo", "zelle", "card"];

const MobilePayment: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: move, isLoading, error, refetch } = useGetMoveQuery(id);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [processFinalPayment] = useProcessFinalPaymentMutation();
  const [createPaymentLink] = useCreatePaymentLinkMutation();
  const [paymentLink, setPaymentLink] = useState<PaymentLink | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const selectedMover = useSelector(getSelectedMover);
  const [processingFeeAmount, setProcessingFeeAmount] = useState(0);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const inApp = useSelector(checkMobileToken);

  const amountDue = useMemo(() => {
    if (!move?.invoice) return 0;
    return Number(move.invoice.due_amount) + Number(processingFeeAmount);
  }, [move?.invoice, processingFeeAmount]);

  useEffect(() => {
    // Save the original viewport meta tag content
    const originalViewport = document
      .querySelector('meta[name="viewport"]')
      ?.getAttribute("content");

    // Set the new viewport meta tag to prevent zooming
    document
      .querySelector('meta[name="viewport"]')
      ?.setAttribute(
        "content",
        "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
      );

    // Restore the original viewport meta tag when component unmounts
    return () => {
      if (originalViewport) {
        document
          .querySelector('meta[name="viewport"]')
          ?.setAttribute("content", originalViewport);
      }
    };
  }, []);

  const handleSelection = async (method: string) => {
    let processingFeePercentage = 0;
    let processingFeeAmount = 0;

    if (!move?.id || !selectedMover) {
      throw new Error("Move information not available");
    }

    if (NO_FEE_PAYMENT_METHODS.includes(method)) {
      setProcessingFeeAmount(0);
    } else {
      processingFeePercentage =
        selectedMover.mover_configuration?.cc_fee_percentage || 0;
      processingFeeAmount =
        processingFeePercentage > 0
          ? (move.invoice.due_amount * processingFeePercentage) / 100
          : 0;
      setProcessingFeeAmount(processingFeeAmount);
    }

    if (method === "link") {
      const response = await createPaymentLink({
        move_id: move.id,
        payment_link: {
          purpose: "final_payment",
          amount: Number(move.invoice.due_amount) + Number(processingFeeAmount),
          processing_fee_percentage: processingFeePercentage,
          processing_fee_amount: processingFeeAmount,
        },
      }).unwrap();

      // Store the payment link and open the drawer to show QR code
      setPaymentLink(response);
      setDrawerOpen(true);
    }
  };

  const handleSubmit = useCallback(async () => {
    if (!selectedPaymentMethod) {
      setErrorMessage("Please select a payment method");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      // Special handling for payment link
      if (selectedPaymentMethod === "link") {
        // Send success message to React Native
        // @ts-expect-error - window["ReactNativeWebView"] is not defined in the browser
        if (window["ReactNativeWebView"]) {
          // @ts-expect-error - window["ReactNativeWebView"] is not defined in the browser
          window["ReactNativeWebView"].postMessage(
            JSON.stringify({
              type: "payment_link_created",
              success: true,
            })
          );
        }
        // Link handling might need adjustment based on actual flow - this assumes link creation is separate
        // For now, we might just want to show a message or do nothing further in handleSubmit for 'link'
        setPaymentSuccess(true); // Or maybe not? Depends on if link creation itself means success here
      } else {
        // Handle other payment methods with existing code
        const isExistingPaymentMethod = !isNaN(Number(selectedPaymentMethod));

        await processFinalPayment({
          moveId: move.id,
          amount: amountDue,
          processingFeeAmount: processingFeeAmount,
          paymentMethod: isExistingPaymentMethod
            ? { id: Number(selectedPaymentMethod) }
            : {
                kind: selectedPaymentMethod,
              },
        }).unwrap();

        await refetch();

        setPaymentSuccess(true);

        // @ts-expect-error - window["ReactNativeWebView"] is not defined in the browser
        if (window["ReactNativeWebView"]) {
          // @ts-expect-error - window["ReactNativeWebView"] is not defined in the browser
          window["ReactNativeWebView"].postMessage(
            JSON.stringify({
              type: "payment_successful",
              success: true,
            })
          );
        }
      }
    } catch (error) {
      setErrorMessage("Failed to process payment. Please try again.");

      // @ts-expect-error - window["ReactNativeWebView"] is not defined in the browser
      if (window["ReactNativeWebView"]) {
        // @ts-expect-error - window["ReactNativeWebView"] is not defined in the browser
        window["ReactNativeWebView"].postMessage(
          JSON.stringify({
            type: "payment_failed",
            success: false,
            error: error instanceof Error ? error.message : "Unknown error",
          })
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    selectedPaymentMethod,
    setErrorMessage,
    setIsSubmitting,
    move,
    processFinalPayment,
    amountDue,
    processingFeeAmount,
    refetch,
    setPaymentSuccess,
  ]);

  useEffect(() => {
    window.addEventListener("submitPayment", handleSubmit as EventListener);

    return () => {
      window.removeEventListener(
        "submitPayment",
        handleSubmit as EventListener
      );
    };
  }, [handleSubmit]);

  useEffect(() => {
    if (selectedPaymentMethod) {
      // @ts-expect-error - window["ReactNativeWebView"] is not defined in the browser
      if (window["ReactNativeWebView"]) {
        // @ts-expect-error - window["ReactNativeWebView"] is not defined in the browser
        window["ReactNativeWebView"].postMessage(
          JSON.stringify({
            type: "payment_method_selected",
          })
        );
      }
    }
  }, [selectedPaymentMethod]);

  if (isLoading)
    return (
      <div className="p-4">
        <Loading />
      </div>
    );
  if (error)
    return <div className="p-4 text-red-500">Error loading move data</div>;
  if (!move) return <div className="p-4">Move not found</div>;

  if (paymentSuccess) {
    return (
      <div className="p-4 max-w-md mx-auto overflow-y-auto flex flex-col gap-4">
        <div className="text-xl font-bold flex flex-row items-center gap-2">
          <FontAwesomeIcon
            icon={faCheckCircle}
            className="text-green-600 text-xl"
          />
          <span className="text-xl font-bold">Payment Successful!</span>
        </div>

        <div className="text-base">
          A receipt has been sent to the customer's email.
        </div>
      </div>
    );
  }

  if (amountDue === 0) {
    return (
      <div className="p-4 max-w-md mx-auto overflow-y-auto">
        <h1 className="text-2xl font-bold">Nothing to pay</h1>
        <p className="text-gray-500">
          The customer has already paid for this move.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-md mx-auto overflow-y-auto">
      {/* <h1 className="text-xl font-bold mb-6">Payment</h1> */}

      <div className="mb-4 bg-stone-100 p-4 rounded-lg">
        <h2 className="text-lg font-medium mb-2">Amount Due</h2>
        <div className="text-3xl font-bold flex flex-col gap-2">
          {Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
          }).format(amountDue)}
          {processingFeeAmount > 0 ? (
            <div className="text-sm font-normal">
              {Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(processingFeeAmount)}{" "}
              card processing fee included
            </div>
          ) : (
            <div className="text-sm font-normal">No processing fee</div>
          )}
        </div>
      </div>

      <div className="mb-6">
        <h2 className="text-xl font-medium mb-4">
          How is the customer paying?
        </h2>

        {errorMessage && (
          <div className="font-semibold text-red-600 mb-4">{errorMessage}</div>
        )}

        <RadioGroup
          value={selectedPaymentMethod}
          onValueChange={setSelectedPaymentMethod}
          className="space-y-3"
        >
          {/* Existing payment methods */}
          {move.rechargeable_payment_methods &&
            move.rechargeable_payment_methods.length > 0 && (
              <>
                <div className="text-sm text-gray-500">Previously Used</div>
                {move.rechargeable_payment_methods.map((pm: PaymentMethod) => (
                  <div
                    key={pm.id}
                    onClick={() => {
                      handleSelection(pm.id.toString());
                    }}
                  >
                    <RadioGroupItem
                      value={pm.id.toString()}
                      id={`pm-${pm.id}`}
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor={`pm-${pm.id}`}
                      className="flex flex-row items-center gap-4 rounded-lg border-2 border-muted bg-popover px-4 py-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                    >
                      <CreditCard className="h-5 w-5" />
                      <div className="flex-1 text-lg font-medium">
                        {
                          CARD_BRANDS[
                            pm.brand.toLowerCase() as keyof typeof CARD_BRANDS
                          ]
                        }{" "}
                        <span className="text-sm font-normal">ending in</span>{" "}
                        {pm.last_4}{" "}
                        <span className="text-sm text-gray-700 font-normal">
                          (exp {pm.exp_month}/{pm.exp_year})
                        </span>
                        {selectedMover?.mover_configuration
                          ?.cc_fee_percentage &&
                          selectedMover?.mover_configuration
                            ?.cc_fee_percentage > 0 && (
                            <div className="text-sm font-normal text-amber-600">
                              {
                                selectedMover?.mover_configuration
                                  ?.cc_fee_percentage
                              }
                              % card processing fee applicable
                            </div>
                          )}
                      </div>
                    </Label>
                  </div>
                ))}
              </>
            )}

          <div className="text-sm text-gray-500 mt-4 mb-2">
            {move.rechargeable_payment_methods &&
              move.rechargeable_payment_methods.length > 0 &&
              "More"}{" "}
            Options
          </div>

          {selectedMover?.mover_configuration.enabled_for_payments && (
            <div
              onClick={() => {
                handleSelection("link");
              }}
            >
              <RadioGroupItem value="link" id="link" className="peer sr-only" />
              <Label
                htmlFor="link"
                className="flex flex-row items-center gap-4 rounded-lg border-2 border-muted bg-popover px-4 py-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
              >
                <CreditCard className="h-5 w-5" />
                <div className="flex-1 text-lg font-medium">
                  Get Payment Link{" "}
                  <span className="text-sm font-normal">
                    (via QR code/text/email)
                  </span>
                  {selectedMover?.mover_configuration?.cc_fee_percentage &&
                    selectedMover?.mover_configuration?.cc_fee_percentage >
                      0 && (
                      <div className="text-sm font-normal text-amber-600">
                        {selectedMover?.mover_configuration?.cc_fee_percentage}%
                        card processing fee applicable
                      </div>
                    )}
                </div>
              </Label>
            </div>
          )}

          <div
            onClick={() => {
              handleSelection("card");
            }}
          >
            <RadioGroupItem value="card" id="card" className="peer sr-only" />
            <Label
              htmlFor="card"
              className="flex flex-row items-center gap-4 rounded-lg border-2 border-muted bg-popover px-4 py-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
            >
              <CreditCard className="h-5 w-5" />
              <div className="flex-1 text-lg font-medium">Card (swipe/tap)</div>
            </Label>
          </div>

          <div
            onClick={() => {
              handleSelection("cash");
            }}
          >
            <RadioGroupItem value="cash" id="cash" className="peer sr-only" />
            <Label
              htmlFor="cash"
              className="flex flex-row items-center gap-4 rounded-lg border-2 border-muted bg-popover px-4 py-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
            >
              <DollarSign className="h-5 w-5" />
              <div className="flex-1 text-lg font-medium">Cash</div>
            </Label>
          </div>

          <div
            onClick={() => {
              handleSelection("check");
            }}
          >
            <RadioGroupItem value="check" id="check" className="peer sr-only" />
            <Label
              htmlFor="check"
              className="flex flex-row items-center gap-4 rounded-lg border-2 border-muted bg-popover px-4 py-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
            >
              <CheckSquare className="h-5 w-5" />
              <div className="flex-1 text-lg font-medium">Check</div>
            </Label>
          </div>

          <div
            onClick={() => {
              handleSelection("venmo");
            }}
          >
            <RadioGroupItem value="venmo" id="venmo" className="peer sr-only" />
            <Label
              htmlFor="venmo"
              className="flex flex-row items-center gap-4 rounded-lg border-2 border-muted bg-popover px-4 py-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
            >
              <Smartphone className="h-5 w-5" />
              <div className="flex-1 text-lg font-medium">Venmo</div>
            </Label>
          </div>

          <div
            onClick={() => {
              handleSelection("zelle");
            }}
          >
            <RadioGroupItem value="zelle" id="zelle" className="peer sr-only" />
            <Label
              htmlFor="zelle"
              className="flex flex-row items-center gap-4 rounded-lg border-2 border-muted bg-popover px-4 py-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
            >
              <Smartphone className="h-5 w-5" />
              <div className="flex-1 text-lg font-medium">Zelle</div>
            </Label>
          </div>
        </RadioGroup>
      </div>

      {!inApp && (
        <Button
          onClick={handleSubmit}
          disabled={!selectedPaymentMethod}
          className={cn("w-full", !selectedPaymentMethod && "opacity-50")}
        >
          {isSubmitting ? "Processing..." : "Submit Payment"}
        </Button>
      )}

      {/* Payment Link QR Code Drawer */}
      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DrawerContent className="px-4">
          <DrawerHeader className="text-left">
            <DrawerTitle className="text-xl font-bold">
              Payment Link
            </DrawerTitle>
            <DrawerDescription className="text-base">
              Share this QR code to the customer so they can pay on their phone.
            </DrawerDescription>
          </DrawerHeader>
          <div className="p-4 flex flex-col items-center justify-center gap-4">
            {paymentLink?.full_url ? (
              <>
                <div className="text-xl font-bold flex flex-col items-center gap-2">
                  {Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(amountDue)}
                  {processingFeeAmount > 0 && (
                    <div className="text-sm font-normal">
                      {Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                      }).format(processingFeeAmount)}{" "}
                      card processing fee included
                    </div>
                  )}
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <QRCodeSVG value={paymentLink.full_url} size={200} />
                </div>
                {/* <div className="text-sm text-center break-all">
                  {paymentLink.full_url}
                </div> */}
                {/* <Button
                  variant="outline"
                  className="flex items-center gap-2 px-4"
                  onClick={() => {
                    alert(
                      "Send payment link functionality will be implemented later"
                    );
                  }}
                >
                  <Send className="h-4 w-4" /> Send Payment Link
                </Button> */}
              </>
            ) : (
              <div className="text-center py-8">Loading payment link...</div>
            )}
          </div>
          <DrawerFooter>
            <DrawerClose asChild>
              <Button variant="secondary">Close</Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
};

export default MobilePayment;
